import { isEmpty } from '@fruitful/fruitils';
import { stripMoneyMapAccountFromRules } from '@fruitful/money-map';
import {
    type DeepPartial,
    MoneyMapAccountStatus,
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    MoneyMapRuleType,
    type MoneyMapRuleUpsertDto,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import {
    type SubmitHandler,
    useForm,
    type UseFormReturn,
} from 'react-hook-form';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    Form,
    H6Emphasized,
    USE_FORM_MODE_DEFAULTS,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { type z } from 'zod';

import { BACKDOOR_ROTH_ENABLED_IN_GROVE } from '../../../config/FruitfulEnvironment';
import { selectedMembershipAtom } from '../../../entities/entityAtoms';
import { useMoneyMapAccounts } from '../../providers/MoneyMapAccountsProvider';
import {
    getBackdoorRothPanelVisibility,
    resolveBackdoorRothFormAccounts,
    resolveExistingMoneyMapTransferAccountForDefaults,
} from '../../utils/backdoorRothFormShared';
import { isAccountBackdoorTrad } from '../../utils/goalFormBackdoorRothUtils';
import { buildMoneyMapAccountForMoneyMapRuleUpsertDto } from '../../utils/moneyMapRuleUpsertDtoBuilder';
import { getDefaultAccountSettings } from '../../utils/moneyMapUtils';
import {
    createOtherAccountsFormSuperRefine,
    otherAccountsFormSchemaBase,
} from '../../utils/otherAccountsFormSchema';
import {
    AccountSettingsFormModule,
    type GenericAccountSettingsFormSchema,
} from './AccountSettingsFormModule';
import { BackdoorRothAccountFields } from './BackdoorRothAccountFields';
import FormDrawer from './FormDrawer';

interface OtherAccountsFormProps {
    rule?: DeepPartial<MoneyMapRuleWithAccountDetails>;
    priority: number;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onPressSave: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<void>;
    onPressRemove: (ruleId?: string) => Promise<void>;
    onClose?: () => void;
    presentation?: 'drawer' | 'modal';
}

const OtherAccountsForm = ({
    rule,
    priority,
    moneyMap,
    onPressSave,
    onPressRemove,
    onClose,
    presentation,
}: OtherAccountsFormProps) => {
    const { accounts } = useMoneyMapAccounts();
    const membership = useAtomValue(selectedMembershipAtom);
    const isSoloMembership = membership?.users?.length === 1;

    const backdoorTradAccountIds = useMemo(
        () => new Set(accounts.filter(isAccountBackdoorTrad).map((a) => a.id)),
        [accounts],
    );

    const {
        backdoorSplitFromRule,
        primaryAccountForFormDefaults,
        transferAccountPreset,
    } = useMemo(
        () =>
            resolveBackdoorRothFormAccounts(
                rule?.moneyMapAccount,
                undefined,
                accounts,
            ),
        [rule?.moneyMapAccount, accounts],
    );

    const selectedAccount =
        rule?.moneyMapAccountId != null
            ? accounts.find((account) => account.id === rule.moneyMapAccountId)
            : rule?.moneyMapAccount;

    const defaultPrimaryAccount =
        primaryAccountForFormDefaults ??
        selectedAccount ??
        rule?.moneyMapAccount;

    const existingAccountForTransfer = useMemo(
        () =>
            resolveExistingMoneyMapTransferAccountForDefaults({
                backdoorSplitFromRule,
                ruleMoneyMapAccount: rule?.moneyMapAccount,
                transferAccountPreset,
                accounts,
            }),
        [
            accounts,
            backdoorSplitFromRule,
            rule?.moneyMapAccount,
            transferAccountPreset,
        ],
    );

    const preserveTransferWhenBackdoorUiSuppressed =
        !BACKDOOR_ROTH_ENABLED_IN_GROVE && backdoorSplitFromRule.isBackdoorRoth;

    const formSchema = useMemo(
        () =>
            otherAccountsFormSchemaBase.superRefine(
                createOtherAccountsFormSuperRefine(accounts, isSoloMembership),
            ),
        [accounts, isSoloMembership],
    );

    type OtherAccountsFormSchema = z.infer<typeof otherAccountsFormSchemaBase>;

    const defaultTransferAccountSettings = useMemo(
        () => getDefaultAccountSettings(existingAccountForTransfer, true),
        [existingAccountForTransfer],
    );

    const formFunctions = useForm<OtherAccountsFormSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(formSchema),
        defaultValues: {
            accountSettings: getDefaultAccountSettings(defaultPrimaryAccount),
            transferAccountSettings: defaultTransferAccountSettings,
            isBackdoorRoth: backdoorSplitFromRule.isBackdoorRoth,
        },
    });

    const selectedMoneyMapAccountId = formFunctions.watch(
        'accountSettings.moneyMapAccountId',
    );

    const selectedMoneyMapAccount = useMemo(() => {
        return accounts.find(
            (account) => account.id === selectedMoneyMapAccountId,
        );
    }, [accounts, selectedMoneyMapAccountId]);

    const selectedMoneyMapAccountIdForTransfer = formFunctions.watch(
        'transferAccountSettings.moneyMapAccountId',
    );

    const selectedMoneyMapAccountForTransfer = useMemo(() => {
        return accounts.find(
            (account) => account.id === selectedMoneyMapAccountIdForTransfer,
        );
    }, [accounts, selectedMoneyMapAccountIdForTransfer]);

    const onSubmit: SubmitHandler<OtherAccountsFormSchema> = async ({
        accountSettings,
        transferAccountSettings,
        isBackdoorRoth,
    }) => {
        if (accountSettings.moneyMapAccountId == null) {
            return;
        }

        const proposedAccountToUpdate = accounts.find(
            (account) =>
                account.id === accountSettings.moneyMapAccountId &&
                account.status === MoneyMapAccountStatus.PROPOSED,
        );
        const proposedTransferAccountToUpdate = accounts.find(
            (account) =>
                account.id === transferAccountSettings?.moneyMapAccountId &&
                account.status === MoneyMapAccountStatus.PROPOSED,
        );

        const accountName =
            accountSettings.name ??
            selectedMoneyMapAccount?.accountDetails.name ??
            rule?.description ??
            'Other account';

        const updatedRule: MoneyMapRuleUpsertDto = {
            id: rule?.id,
            moneyMapId: moneyMap.id,
            type: MoneyMapRuleType.ACCOUNT_ONLY,
            description: accountName,
            priority,
            routingRuleType: null,
            fixedAmountCents: null,
            percentage: null,
            targetBalanceCents: null,
            monthlyContributionMaxCents: null,
            yearlyContributionMaxCents: null,
        };

        const updatedRuleWithAccount =
            buildMoneyMapAccountForMoneyMapRuleUpsertDto(
                updatedRule,
                { accountSettings },
                { transferAccountSettings },
                MoneyMapCategory.OTHER_ACCOUNTS,
                membership,
                selectedMoneyMapAccount,
                proposedAccountToUpdate,
                proposedTransferAccountToUpdate,
                undefined,
                { isBackdoorRoth: isBackdoorRoth === true },
                selectedMoneyMapAccountForTransfer,
            );

        const existingRules = stripMoneyMapAccountFromRules(
            moneyMap.moneyMapRules ?? [],
        );

        const updatedRules = [...existingRules];
        const existingRuleIndex = updatedRules.findIndex(
            (existingRule) => existingRule.id === updatedRuleWithAccount.id,
        );

        if (existingRuleIndex >= 0) {
            updatedRules[existingRuleIndex] = updatedRuleWithAccount;
        } else {
            updatedRules.push(updatedRuleWithAccount);
        }

        await onPressSave({
            moneyMapRules: updatedRules,
        });
    };

    const handleSubmit = formFunctions.handleSubmit;
    const isValid = formFunctions.formState.isValid;

    const isBackdoorRothWatch = formFunctions.watch('isBackdoorRoth');
    const apexRothTypeWatch = formFunctions.watch(
        'accountSettings.apexInvestmentAccountType',
    );

    const {
        shouldShowBackdoorCheckbox,
        shouldShowBackdoorTradPanel,
        shouldDisableBackdoorCheckbox,
        shouldForceBackdoorRothChecked,
        isKnownBackdoorRoth,
    } = getBackdoorRothPanelVisibility({
        isBackdoorRothWatch,
        selectedMoneyMapAccountId,
        selectedMoneyMapAccount,
        apexRothTypeWatch,
        accounts,
    });

    useEffect(() => {
        if (preserveTransferWhenBackdoorUiSuppressed) {
            return;
        }
        if (!shouldShowBackdoorTradPanel) {
            formFunctions.setValue('transferAccountSettings', undefined);
        }
    }, [
        preserveTransferWhenBackdoorUiSuppressed,
        shouldShowBackdoorTradPanel,
        formFunctions,
    ]);

    useEffect(() => {
        if (!shouldShowBackdoorTradPanel) {
            return;
        }
        const transfer = formFunctions.getValues('transferAccountSettings');
        const needsHydration =
            transfer == null ||
            isEmpty(transfer) ||
            transfer.moneyMapAccountId == null ||
            transfer.moneyMapAccountId === '';
        if (!needsHydration) {
            return;
        }
        if (defaultTransferAccountSettings != null) {
            formFunctions.setValue(
                'transferAccountSettings',
                defaultTransferAccountSettings,
                {
                    shouldValidate: true,
                    shouldDirty: false,
                },
            );
        }
    }, [
        shouldShowBackdoorTradPanel,
        defaultTransferAccountSettings,
        formFunctions,
    ]);

    useEffect(() => {
        if (!BACKDOOR_ROTH_ENABLED_IN_GROVE) {
            return;
        }
        if (!shouldShowBackdoorCheckbox && isBackdoorRothWatch) {
            formFunctions.setValue('isBackdoorRoth', false);
        }
    }, [
        BACKDOOR_ROTH_ENABLED_IN_GROVE,
        shouldShowBackdoorCheckbox,
        isBackdoorRothWatch,
        formFunctions,
    ]);

    useEffect(() => {
        if (!isKnownBackdoorRoth) {
            return;
        }
        if (shouldForceBackdoorRothChecked && !isBackdoorRothWatch) {
            formFunctions.setValue('isBackdoorRoth', true, {
                shouldValidate: true,
            });
        }
        const pairedTrad = accounts.find(
            (a) =>
                a.backdoorRothLinkedMoneyMapAccountId ===
                selectedMoneyMapAccountId,
        );
        if (pairedTrad != null) {
            const currentTransferId = formFunctions.getValues(
                'transferAccountSettings.moneyMapAccountId',
            );
            if (currentTransferId !== pairedTrad.id) {
                const tradDefaults = getDefaultAccountSettings(
                    pairedTrad,
                    true,
                );
                formFunctions.setValue(
                    'transferAccountSettings',
                    tradDefaults,
                    { shouldValidate: true },
                );
            }
        }
    }, [
        isKnownBackdoorRoth,
        shouldForceBackdoorRothChecked,
        isBackdoorRothWatch,
        formFunctions,
        accounts,
        selectedMoneyMapAccountId,
    ]);

    useEffect(() => {
        const subscription = formFunctions.watch(() => {
            void formFunctions.trigger();
        });

        void formFunctions.trigger();

        return () => {
            subscription.unsubscribe();
        };
    }, [
        formFunctions,
        selectedMoneyMapAccountId,
        selectedMoneyMapAccountIdForTransfer,
        isBackdoorRothWatch,
        shouldForceBackdoorRothChecked,
    ]);

    return (
        <FormDrawer
            onClose={onClose}
            presentation={presentation}
            onSave={handleSubmit(onSubmit)}
            saveDisabled={!isValid}
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <YStack gap={16}>
                    <XStack alignItems="center" justifyContent="space-between">
                        <View flexGrow={1}>
                            <H6Emphasized>
                                {rule?.id != null
                                    ? 'Edit other account'
                                    : 'Add other account'}
                            </H6Emphasized>
                        </View>
                        {rule?.id != null && (
                            <Button
                                variant={ButtonVariant.TERTIARY}
                                size={ButtonSize.TINY}
                                width="auto"
                                tone="danger"
                                text="Delete"
                                onPress={() => {
                                    void onPressRemove(rule.id);
                                }}
                            />
                        )}
                    </XStack>
                    <AccountSettingsFormModule
                        type="account"
                        accounts={accounts}
                        selectedAccount={
                            (selectedMoneyMapAccount ??
                                selectedAccount ??
                                undefined) as
                                | MoneyMapAccountWithAccountDetails
                                | undefined
                        }
                        formFunctions={
                            formFunctions as never as UseFormReturn<GenericAccountSettingsFormSchema>
                        }
                        ruleType={MoneyMapRuleType.ACCOUNT_ONLY}
                        disabledAccountIds={backdoorTradAccountIds}
                    />
                    <BackdoorRothAccountFields
                        accounts={accounts}
                        formFunctions={
                            formFunctions as never as UseFormReturn<
                                GenericAccountSettingsFormSchema & {
                                    isBackdoorRoth?: boolean;
                                }
                            >
                        }
                        ruleType={MoneyMapRuleType.ACCOUNT_ONLY}
                        shouldShowBackdoorCheckbox={shouldShowBackdoorCheckbox}
                        shouldShowBackdoorTradPanel={
                            shouldShowBackdoorTradPanel
                        }
                        shouldDisableBackdoorCheckbox={
                            shouldDisableBackdoorCheckbox
                        }
                        shouldForceBackdoorRothChecked={
                            shouldForceBackdoorRothChecked
                        }
                        selectedMoneyMapAccount={selectedMoneyMapAccount}
                        selectedMoneyMapAccountForTransfer={
                            selectedMoneyMapAccountForTransfer
                        }
                        isUsingPresetTransfer={false}
                    />
                </YStack>
            </Form>
        </FormDrawer>
    );
};

export default OtherAccountsForm;
